import { useCallback, useMemo, useState } from "react";
import {
  View,
  Text,
  Pressable,
  Image,
  Modal,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  X,
  Clock,
  Calendar,
  MapPin,
  Video,
  FileText,
  ClipboardList,
  Info,
  AlertTriangle,
  Syringe,
  Timer,
  Heart,
  CalendarCheck,
  Link,
  ChevronRight,
  CheckCircle2,
  Stethoscope,
  ClipboardCheck,
  BookOpen,
  TestTube2,
} from "lucide-react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import {
  getProgramsContainingModule,
  getRelatedBlocks,
  type ContentBlock,
  type ContentBlockId,
} from "@src/packages/content";
import { useDeleteAppointment } from "@src/packages/care";
import { canManageAppointment } from "@src/packages/care/appointment-ownership";
import { useApiClient } from "@src/packages/api/louise-api";
import { useLocale, useTranslations } from "@src/packages/i18n";
import type { AppointmentScheduleItem } from "../screens/care/schedule.types";
import { localizeAppointmentTitle } from "../screens/care/localizeAppointmentTitle";
import { EMBER } from "../../onboarding/theme";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import {
  ActionBanner,
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  IconButton,
  MoonDivider,
  SecondaryButton,
  SectionTitle,
  TextActionButton,
} from "@louise/moon-ui";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { FactSheetBadge } from "./FactSheetHeader";
import EvidenceLevelModal from "./EvidenceLevelModal";
import LinkedFactSheetText, {
  type FactSheetLinkTarget,
} from "./LinkedFactSheetText";
import EvidenceSourceRows from "./EvidenceSourceRows";
import { getProcedureIllustration } from "./access/procedureIllustrations";
import { semanticColors, withAlpha } from "@louise/theme/tokens";

const DELETE_CONFIRMATION_ILLUSTRATION =
  require("../../assets/images/you-icons/trash-bin.png") as number;

type Props = {
  item: ContentBlock<"procedure"> | null;
  onClose: () => void;
  appointment?: AppointmentScheduleItem & { date?: string };
  onEdit?: (appt: AppointmentScheduleItem) => void;
  onOpenProgram?: (programId: string) => void;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
};

const SPRING_CONFIG = { damping: 20, stiffness: 200, mass: 0.8 };

const PROCEDURE_MODULE_IDS: Record<string, string> = {
  "proc-hormonal-testing": "module-female-hormone-baseline",
  "proc-ovarian-stimulation": "module-ovarian-stimulation",
  "proc-egg-retrieval": "module-egg-retrieval",
  "proc-embryo-transfer": "module-embryo-transfer",
  "proc-iui": "module-iui-procedure",
  "proc-embryo-biopsy-pgt": "module-pgt-a",
  "proc-carrier-testing": "module-genetic-carrier",
  "proc-two-week-wait": "module-tww-support",
  "proc-exome-genome-sequencing": "module-comprehensive-genomic",
};

function splitProcedureSteps(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((step) => step.trim())
    .filter(Boolean);
}

function formatAnesthesia(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function ProcedureHeaderVisual({ item }: { item: ContentBlock<"procedure"> }) {
  const image = getProcedureIllustration({
    procedureId: item.id,
    category: item.body.category,
  });

  return (
    <View
      style={{
        width: 132,
        height: 112,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Image
        source={image}
        style={{ width: 124, height: 104 }}
        resizeMode="contain"
      />
    </View>
  );
}

function NumberedInstructionRows({
  items,
  currentBlockId,
  onOpenFactSheet,
}: {
  items: string[];
  currentBlockId: string;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  if (!items.length) return null;

  return (
    <View
      style={{
        borderRadius: 8,
        borderWidth: 1,
        borderColor: semanticColors.border.subtle,
        backgroundColor: semanticColors.surface.glass,
        overflow: "hidden",
        marginBottom: 16,
      }}
    >
      {items.map((item, i) => (
        <View
          key={`${item}-${i}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingHorizontal: 12,
            paddingVertical: 12,
            borderTopWidth: i === 0 ? 0 : 1,
            borderTopColor: semanticColors.border.subtle,
          }}
        >
          <View
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: withAlpha(semanticColors.text.secondary, 0.1),
              marginTop: 1,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "700",
                color: EMBER.colors.textMid,
              }}
            >
              {i + 1}
            </Text>
          </View>
          <LinkedFactSheetText
            text={item}
            currentBlockId={currentBlockId}
            onOpenLink={onOpenFactSheet}
            style={{
              flex: 1,
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          />
        </View>
      ))}
    </View>
  );
}

function IconListRows({
  items,
  icons,
  currentBlockId,
  onOpenFactSheet,
}: {
  items: string[];
  icons: (typeof Info)[];
  currentBlockId: string;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  if (!items.length) return null;

  return (
    <View
      style={{
        borderRadius: 8,
        borderWidth: 1,
        borderColor: semanticColors.border.subtle,
        backgroundColor: semanticColors.surface.glass,
        overflow: "hidden",
        marginBottom: 16,
      }}
    >
      {items.map((item, i) => {
        const Icon = icons[i % icons.length] ?? Info;
        return (
          <View
            key={`${item}-${i}`}
            style={{
              flexDirection: "row",
              gap: 10,
              paddingHorizontal: 12,
              paddingVertical: 12,
              borderTopWidth: i === 0 ? 0 : 1,
              borderTopColor: semanticColors.border.subtle,
            }}
          >
            <View
              style={{
                width: 24,
                height: 24,
                borderRadius: 12,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: withAlpha(semanticColors.text.secondary, 0.1),
                marginTop: 1,
              }}
            >
              <Icon size={13} color={EMBER.colors.textMid} strokeWidth={1.8} />
            </View>
            <LinkedFactSheetText
              text={item}
              currentBlockId={currentBlockId}
              onOpenLink={onOpenFactSheet}
              style={{
                flex: 1,
                fontSize: 13,
                color: EMBER.colors.textLight,
                lineHeight: 18,
              }}
            />
          </View>
        );
      })}
    </View>
  );
}

function DetailInfoRows({
  rows,
  icons,
  currentBlockId,
  onOpenFactSheet,
}: {
  rows: { label: string; detail: string }[];
  icons: (typeof Info)[];
  currentBlockId: string;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  if (!rows.length) return null;

  return (
    <View
      style={{
        borderRadius: 8,
        borderWidth: 1,
        borderColor: semanticColors.border.subtle,
        backgroundColor: semanticColors.surface.glass,
        overflow: "hidden",
        marginBottom: 16,
      }}
    >
      {rows.map((row, i) => {
        const Icon = icons[i % icons.length] ?? Info;
        return (
          <View
            key={`${row.label}-${i}`}
            style={{
              flexDirection: "row",
              gap: 10,
              paddingHorizontal: 12,
              paddingVertical: 12,
              borderTopWidth: i === 0 ? 0 : 1,
              borderTopColor: semanticColors.border.subtle,
            }}
          >
            <View
              style={{
                width: 24,
                height: 24,
                borderRadius: 12,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: withAlpha(semanticColors.text.secondary, 0.1),
                marginTop: 1,
              }}
            >
              <Icon size={13} color={EMBER.colors.textMid} strokeWidth={1.8} />
            </View>
            <View style={{ flex: 1 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                  lineHeight: 18,
                  marginBottom: 2,
                }}
              >
                {row.label}
              </Text>
              <LinkedFactSheetText
                text={row.detail}
                currentBlockId={currentBlockId}
                onOpenLink={onOpenFactSheet}
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  lineHeight: 18,
                }}
              />
            </View>
          </View>
        );
      })}
    </View>
  );
}

export function ProcedureDetailContent({
  item,
  onClose,
  appointment,
  onEdit,
  skipExitAnimation,
  onOpenProgram,
  onOpenFactSheet,
}: {
  item: ContentBlock<"procedure">;
  onClose: () => void;
  appointment?: AppointmentScheduleItem & { date?: string };
  onEdit?: (appt: AppointmentScheduleItem) => void;
  skipExitAnimation?: boolean;
  onOpenProgram?: (programId: string) => void;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("ProcedureDetailContent");
  const insets = useSafeAreaInsets();
  const deleteAppointment = useDeleteAppointment();
  const { patientId } = useApiClient();
  // One shared ownership predicate across every appointment-backed sheet
  // (#2232): fails closed when an adapter dropped ownership metadata.
  const canManage = canManageAppointment(appointment, patientId);
  const canEdit = canManage && Boolean(onEdit);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [evidenceModal, setEvidenceModal] = useState<string | null>(null);
  const scale = useSharedValue(0.92);
  const opacity = useSharedValue(0);
  const [scrollPercent, setScrollPercent] = useState(0);

  useState(() => {
    scale.value = withSpring(1, SPRING_CONFIG);
    opacity.value = withTiming(1, { duration: 250 });
  });

  const animatedStyle = useAnimatedStyle(() => ({
    flex: 1,
    overflow: "hidden" as const,
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const handleClose = useCallback(() => {
    if (skipExitAnimation) {
      onClose();
      return;
    }
    scale.value = withSpring(0.92, SPRING_CONFIG);
    opacity.value = withTiming(0, { duration: 200 }, () => {
      runOnJS(onClose)();
    });
  }, [scale, opacity, onClose, skipExitAnimation]);

  const cancelDelete = () => {
    if (isDeleting) return;
    setConfirmingDelete(false);
    setDeleteError(null);
  };

  const confirmDelete = () => {
    if (!appointment) return;
    setIsDeleting(true);
    setDeleteError(null);
    deleteAppointment.mutate(appointment.appointmentId ?? appointment.id, {
      onSuccess: () => {
        setIsDeleting(false);
        setConfirmingDelete(false);
        handleClose();
      },
      onError: () => {
        setIsDeleting(false);
        // Keep the confirmation open so the same Delete button retries; show
        // why (#2232).
        setDeleteError(t("appointment_detail.delete_error"));
      },
    });
  };

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      const scrollable = contentSize.height - layoutMeasurement.height;
      if (scrollable <= 0) {
        setScrollPercent(100);
        return;
      }
      setScrollPercent(
        Math.min(100, Math.round((contentOffset.y / scrollable) * 100)),
      );
    },
    [],
  );

  const { body } = item;
  const { locale } = useLocale();
  const fr = locale === "fr" ? body.frBody : undefined;
  const description = fr?.description ?? body.description;
  const howItWorks = fr?.howItWorks ?? body.howItWorks;
  const duration = fr?.duration ?? body.duration;
  const recoveryTime = fr?.recoveryTime ?? body.recoveryTime;
  const risks = fr?.risks ?? body.risks;
  const prepInstructions = fr?.prepInstructions ?? body.prepInstructions;
  const whatToExpect = fr?.whatToExpect ?? body.whatToExpect;
  const results = fr?.results ?? body.results;
  const partOfPrograms = useMemo(() => {
    const moduleId = PROCEDURE_MODULE_IDS[item.id];
    if (!moduleId) return [];
    return getProgramsContainingModule(moduleId as ContentBlockId);
  }, [item.id]);
  const relatedTests = useMemo(
    () =>
      getRelatedBlocks(item.id, "related-to")
        .filter(
          ({ relationship, block }) =>
            relationship.from === item.id &&
            (relationship.label === "Can lead to" ||
              relationship.label === "Canonical test" ||
              relationship.label === "Procedure used for" ||
              relationship.label === "Sample collected for") &&
            block.kind === "test",
        )
        .map(({ block }) => block),
    [item.id],
  );

  return (
    <View style={{ flex: 1, overflow: "hidden" }}>
      <Image
        source={require("@louise/moon-ui/assets/images/onboarding-bg.jpg")}
        style={{
          position: "absolute",
          width: "115%",
          height: "115%",
          left: "-7.5%",
          top: "-7.5%",
        }}
        resizeMode="cover"
      />
      <Animated.View style={animatedStyle}>
        <View style={{ flex: 1, paddingTop: insets.top }}>
          {/* Progress bar */}
          <View
            style={{
              height: 3,
              backgroundColor: semanticColors.surface.muted,
              marginHorizontal: 16,
              borderRadius: 2,
            }}
          >
            <View
              style={{
                height: 3,
                width: `${scrollPercent}%`,
                backgroundColor: semanticColors.accent.default,
                borderRadius: 2,
              }}
            />
          </View>

          <Animated.ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
          >
            <View
              style={{
                ...GLASS_CARD_STYLE,
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
              }}
            >
              {/* Close button */}
              <View
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                }}
              >
                <IconButton
                  onPress={handleClose}
                  hitSlop={8}
                  size={32}
                  surface="soft"
                  backgroundColor={semanticColors.surface.muted}
                  pressedBackgroundColor={semanticColors.surface.muted}
                  pressedOpacity={1}
                  accessibilityLabel="Close"
                >
                  <X size={16} color={EMBER.colors.textLight} />
                </IconButton>
              </View>

              {/* Appointment details (when opened from an appointment card) */}
              {appointment && (
                <>
                  <View style={{ marginBottom: 12, marginTop: 8 }}>
                    <SectionTitle icon={CalendarCheck}>
                      {t("procedure_detail.your_appointment")}
                    </SectionTitle>
                  </View>

                  {/* Date row */}
                  {appointment.date && (
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        paddingVertical: 10,
                        borderBottomWidth: 1,
                        borderBottomColor: EMBER.colors.border,
                      }}
                    >
                      <Calendar size={16} color={EMBER.colors.textMid} />
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 14,
                          color: EMBER.colors.textLight,
                          marginLeft: 10,
                          width: 70,
                        }}
                      >
                        {t("appointment_detail.row_date")}
                      </Text>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 14,
                          color: EMBER.colors.text,
                          flex: 1,
                        }}
                      >
                        {appointment.date}
                      </Text>
                    </View>
                  )}

                  {/* Time row */}
                  {appointment.time && (
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        paddingVertical: 10,
                        borderBottomWidth: 1,
                        borderBottomColor: EMBER.colors.border,
                      }}
                    >
                      <Clock size={16} color={EMBER.colors.textMid} />
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 14,
                          color: EMBER.colors.textLight,
                          marginLeft: 10,
                          width: 70,
                        }}
                      >
                        {t("appointment_detail.row_time")}
                      </Text>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 14,
                          color: EMBER.colors.text,
                          flex: 1,
                        }}
                      >
                        {appointment.time}
                      </Text>
                    </View>
                  )}

                  {/* Location row */}
                  {appointment.location && (
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        paddingVertical: 10,
                        borderBottomWidth: 1,
                        borderBottomColor: EMBER.colors.border,
                      }}
                    >
                      <MapPin size={16} color={EMBER.colors.textMid} />
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 14,
                          color: EMBER.colors.textLight,
                          marginLeft: 10,
                          width: 70,
                        }}
                      >
                        {t("appointment_detail.row_location")}
                      </Text>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 14,
                          color: EMBER.colors.text,
                          flex: 1,
                        }}
                      >
                        {appointment.location}
                      </Text>
                    </View>
                  )}

                  {/* Format row */}
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      paddingVertical: 10,
                      borderBottomWidth: 1,
                      borderBottomColor: EMBER.colors.border,
                    }}
                  >
                    <Video size={16} color={EMBER.colors.textMid} />
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        color: EMBER.colors.textLight,
                        marginLeft: 10,
                        width: 70,
                      }}
                    >
                      {t("appointment_detail.row_format")}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        color: EMBER.colors.text,
                        flex: 1,
                      }}
                    >
                      {appointment.isOnline
                        ? t("add_appointment.format_online")
                        : t("add_appointment.format_in_person")}
                    </Text>
                  </View>

                  {/* Notes row */}
                  {appointment.notes && (
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        paddingVertical: 10,
                        borderBottomWidth: 1,
                        borderBottomColor: EMBER.colors.border,
                      }}
                    >
                      <FileText size={16} color={EMBER.colors.textMid} />
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 14,
                          color: EMBER.colors.textLight,
                          marginLeft: 10,
                          width: 70,
                        }}
                      >
                        {t("appointment_detail.row_notes")}
                      </Text>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 14,
                          color: EMBER.colors.text,
                          flex: 1,
                        }}
                      >
                        {appointment.notes}
                      </Text>
                    </View>
                  )}

                  {/* Edit / Delete buttons — only for appointments the
                      patient may manage; the dead Edit (no handler) is gone
                      (#2232). */}
                  {canManage && (
                    <View
                      style={{ flexDirection: "row", gap: 10, marginTop: 16 }}
                    >
                      {canEdit && (
                        <SecondaryButton
                          label={t("appointment_detail.edit")}
                          action="appointment_detail.edit"
                          flex={1}
                          onPress={() => {
                            if (onEdit && appointment) {
                              onEdit(appointment);
                              handleClose();
                            }
                          }}
                        />
                      )}

                      <TextActionButton
                        label={t("appointment_detail.delete")}
                        action="appointment_detail.delete"
                        tone="destructive"
                        onPress={() => {
                          setDeleteError(null);
                          setConfirmingDelete(true);
                        }}
                        style={{
                          flex: 1,
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    </View>
                  )}

                  {/* Divider */}
                  <MoonDivider
                    color={semanticColors.border.subtle}
                    style={{ marginTop: 20, marginBottom: 20 }}
                  />
                </>
              )}

              {/* Procedure illustration */}
              <View
                style={{
                  alignItems: "center",
                  paddingTop: 8,
                  paddingBottom: 20,
                }}
              >
                <ProcedureHeaderVisual item={item} />
              </View>

              {/* Title — centered */}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 24,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                  lineHeight: 30,
                  marginBottom: 8,
                  paddingHorizontal: 24,
                  textAlign: "center",
                }}
              >
                {fr?.procedureName ?? item.title}
              </Text>

              {/* Description */}
              <LinkedFactSheetText
                text={description}
                currentBlockId={item.id}
                onOpenLink={onOpenFactSheet}
                style={{
                  fontSize: 15,
                  color: EMBER.colors.textLight,
                  marginBottom: 14,
                  lineHeight: 22,
                  textAlign: "center",
                }}
              />

              {/* Badge row — centered, matching medication/supplement pattern */}
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 24,
                  justifyContent: "center",
                }}
              >
                {duration && <FactSheetBadge icon={Clock} label={duration} />}
                {body.anesthesia && body.anesthesia !== "none" && (
                  <FactSheetBadge
                    icon={Syringe}
                    label={formatAnesthesia(body.anesthesia)}
                  />
                )}
                {recoveryTime && (
                  <FactSheetBadge
                    icon={Timer}
                    label={t("procedure_detail.recovery_prefix", {
                      params: { value: recoveryTime },
                    })}
                  />
                )}
              </View>

              {/* How it works */}
              <View style={{ marginBottom: 8 }}>
                <SectionTitle icon={Info}>
                  {t("procedure_detail.section_how_it_works")}
                </SectionTitle>
              </View>
              <NumberedInstructionRows
                items={splitProcedureSteps(howItWorks)}
                currentBlockId={item.id}
                onOpenFactSheet={onOpenFactSheet}
              />

              {/* Prep instructions */}
              {prepInstructions?.length ? (
                <>
                  <View style={{ marginBottom: 8 }}>
                    <SectionTitle icon={ClipboardList}>
                      {t("procedure_detail.section_how_to_prepare")}
                    </SectionTitle>
                  </View>
                  <NumberedInstructionRows
                    items={prepInstructions}
                    currentBlockId={item.id}
                    onOpenFactSheet={onOpenFactSheet}
                  />
                </>
              ) : null}

              {/* What to expect */}
              {whatToExpect?.length ? (
                <>
                  <View style={{ marginBottom: 8 }}>
                    <SectionTitle icon={Heart}>
                      {t("procedure_detail.section_what_to_expect")}
                    </SectionTitle>
                  </View>
                  <IconListRows
                    items={whatToExpect}
                    icons={[Clock, Heart, CheckCircle2, Stethoscope]}
                    currentBlockId={item.id}
                    onOpenFactSheet={onOpenFactSheet}
                  />
                </>
              ) : null}

              {/* Results */}
              {results?.length ? (
                <>
                  <View style={{ marginBottom: 8 }}>
                    <SectionTitle icon={ClipboardCheck}>
                      {t("procedure_detail.section_results")}
                    </SectionTitle>
                  </View>
                  <DetailInfoRows
                    rows={results}
                    icons={[Clock, FileText, CalendarCheck, Info]}
                    currentBlockId={item.id}
                    onOpenFactSheet={onOpenFactSheet}
                  />
                </>
              ) : null}

              {/* Canonical tests linked to this procedure */}
              {relatedTests.length && onOpenFactSheet ? (
                <>
                  <View style={{ marginBottom: 8 }}>
                    <SectionTitle icon={TestTube2}>
                      {t("procedure_detail.section_related_tests")}
                    </SectionTitle>
                  </View>
                  <View
                    style={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: 8,
                      marginBottom: 16,
                    }}
                  >
                    {relatedTests.map((test) => (
                      <Pressable
                        key={test.id}
                        accessibilityRole="button"
                        accessibilityLabel={test.title}
                        onPress={() => {
                          // Test names come from the authored content
                          // catalogue, not from the patient's record.
                          capturePress(
                            pressLabel(test.title),
                            "related_test_card",
                          );
                          onOpenFactSheet({
                            kind: "test",
                            id: String(test.id),
                          });
                        }}
                        style={({ pressed }) => ({
                          width: "48%",
                          minHeight: 74,
                          borderRadius: 10,
                          borderWidth: 1,
                          borderColor: semanticColors.border.subtle,
                          backgroundColor: pressed
                            ? semanticColors.overlay.pressed
                            : semanticColors.surface.glass,
                          paddingHorizontal: 12,
                          paddingVertical: 11,
                        })}
                      >
                        <Text
                          style={{
                            color: EMBER.colors.text,
                            fontSize: 14,
                            lineHeight: 18,
                            fontWeight: "700",
                          }}
                        >
                          {test.title}
                        </Text>
                        {test.subtitle ? (
                          <Text
                            numberOfLines={2}
                            style={{
                              color: EMBER.colors.textLight,
                              fontSize: 12,
                              lineHeight: 16,
                              marginTop: 4,
                            }}
                          >
                            {test.subtitle}
                          </Text>
                        ) : null}
                      </Pressable>
                    ))}
                  </View>
                </>
              ) : null}

              {/* Risks */}
              {risks?.length ? (
                <>
                  <View style={{ marginBottom: 8 }}>
                    <SectionTitle icon={AlertTriangle}>
                      {t("procedure_detail.section_risks")}
                    </SectionTitle>
                  </View>
                  <IconListRows
                    items={risks}
                    icons={[AlertTriangle, Heart, Timer, Stethoscope]}
                    currentBlockId={item.id}
                    onOpenFactSheet={onOpenFactSheet}
                  />
                </>
              ) : null}

              {/* Part of */}
              {partOfPrograms.length ? (
                <>
                  <View style={{ marginBottom: 8 }}>
                    <SectionTitle icon={Link}>
                      {t("procedure_detail.section_part_of")}
                    </SectionTitle>
                  </View>
                  <View
                    style={{
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: semanticColors.border.subtle,
                      backgroundColor: semanticColors.surface.glass,
                      overflow: "hidden",
                      marginBottom: 24,
                    }}
                  >
                    {partOfPrograms.map((program, i) => (
                      <Pressable
                        key={program.id}
                        disabled={!onOpenProgram}
                        onPress={() => {
                          // Program titles are authored catalogue copy.
                          capturePress(
                            pressLabel(program.title),
                            "part_of_program_row",
                          );
                          onOpenProgram?.(program.id);
                        }}
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 10,
                          paddingHorizontal: 12,
                          paddingVertical: 12,
                          borderTopWidth: i === 0 ? 0 : 1,
                          borderTopColor: semanticColors.border.subtle,
                        }}
                      >
                        {program.icon ? (
                          <Text style={{ fontSize: 16 }}>{program.icon}</Text>
                        ) : (
                          <Link
                            size={15}
                            color={EMBER.colors.textLight}
                            strokeWidth={1.75}
                          />
                        )}
                        <Text
                          className="font-dm-sans"
                          style={{
                            flex: 1,
                            fontSize: 14,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                            lineHeight: 19,
                          }}
                        >
                          {program.title}
                        </Text>
                        {onOpenProgram ? (
                          <ChevronRight
                            size={15}
                            color={EMBER.colors.textFaint}
                            strokeWidth={1.75}
                          />
                        ) : null}
                      </Pressable>
                    ))}
                  </View>
                </>
              ) : null}

              {/* Evidence */}
              {item.sources?.length ? (
                <View style={{ marginBottom: 24 }}>
                  <SectionTitle icon={BookOpen}>
                    {t("procedure_detail.section_evidence")}
                  </SectionTitle>
                  <EvidenceSourceRows
                    sources={item.sources}
                    onEvidenceLevelPress={setEvidenceModal}
                    style={{ marginTop: 10 }}
                  />
                </View>
              ) : null}

              {/* Disclaimer */}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  color: EMBER.colors.textFaint,
                  lineHeight: 18,
                  textAlign: "center",
                }}
              >
                {t("procedure_detail.disclaimer")}
              </Text>
            </View>
          </Animated.ScrollView>
        </View>
      </Animated.View>

      <EvidenceLevelModal
        level={evidenceModal}
        visible={evidenceModal !== null}
        onClose={() => setEvidenceModal(null)}
      />

      <Dialog
        visible={confirmingDelete && appointment != null}
        onClose={cancelDelete}
        closeButton="minimal"
        dismissOnBackdropTap={!isDeleting}
        testID="procedure-appointment-delete-confirmation-dialog"
      >
        <DialogIcon source={DELETE_CONFIRMATION_ILLUSTRATION} />
        <DialogHeader
          align="center"
          title={t("procedure_detail.delete_confirm")}
          subtitle={t("appointment_detail.delete_confirm", {
            params: {
              title: localizeAppointmentTitle(appointment?.title, t),
            },
          })}
          style={{ marginTop: 16, marginBottom: 20 }}
        />
        {deleteError ? (
          <ActionBanner
            variant="important"
            message={deleteError}
            style={{ marginBottom: 12 }}
          />
        ) : null}
        <DialogActions style={{ marginTop: 0 }}>
          <SecondaryButton
            label={t("appointment_detail.cancel")}
            action="appointment_detail.cancel"
            onPress={cancelDelete}
            disabled={isDeleting}
            flex={1}
          />
          <SecondaryButton
            variant="destructive"
            label={t("appointment_detail.delete")}
            action="procedure_detail.confirm"
            onPress={confirmDelete}
            loading={isDeleting}
            flex={1}
          />
        </DialogActions>
      </Dialog>
    </View>
  );
}

export default function ProcedureDetailSheet({
  item,
  onClose,
  appointment,
  onEdit,
  onOpenProgram,
  onOpenFactSheet,
}: Props) {
  if (!item) return null;
  return (
    <Modal visible transparent animationType="none" onRequestClose={onClose}>
      <ProcedureDetailContent
        item={item}
        onClose={onClose}
        appointment={appointment}
        onEdit={onEdit}
        onOpenProgram={onOpenProgram}
        onOpenFactSheet={onOpenFactSheet}
      />
    </Modal>
  );
}
